import React, { useEffect } from 'react';
import { useHasReachedChatMessageLimit } from '../../../../../../hooks/api/useMedicalProfileUsageMetric';
import useRecordEvent from '../../../../../../hooks/useRecordEvent';
import { View } from 'react-native';
import { APITypes } from '../../../../../../primary-record-types';
import { Loader } from '../../../../../v2BaseComponents/Loader';
import ConversationList, { InitialMessageSentPlaceholderConversationList } from './ConversationList';
import EmptyChatState from './EmptyChatState';
import { CreateChatArgs } from '../../../../../../hooks/api/useChat';
import Stack from '../../../../../v2BaseComponents/Stack';
import { getSpacing } from '../../../../../../utils/spacing';
import MedicalProfileUsageIndicator from '../../../../../shared/MedicalProfileUsageIndicator';
import ChatMessageInput from './ChatMessageInput';
import InformationNotification from './InformationNotification';
import DeleteChatButton from './DeleteChatButton';
import { useIsMobileLayout } from '../../../../../../hooks/useIsMobileLayout';
import { MobileChatListButton } from './ChatList';
import Button from '../../../../../v2BaseComponents/Button';
import { getIntl } from '../../../../../../utils/intl';
import { PRScrollView } from '../../../../../shared/Scroll';

const intl = getIntl('ChatArea', {
    addChat: 'New',
});

export type ChatAreaOwnProps = {
    medicalProfileId: string;
    isLoadingChat: boolean;
    chat: APITypes.chatService.MedicalProfileChat | null;
    isWaitingOnChatMessage: boolean;
    isCreatingChat: boolean;
    onCreateChat: (args: CreateChatArgs) => void;
    onCreateChatMessage: (message: string) => void;
    isDeletingChat: boolean;
    onDeleteChat: () => void;
    chats: APITypes.chatService.ListMedicalProfileChatsResponse;
    selectedChatId: string | null;
    onSelectChat: (chatId: string | null) => void;
    defaultChatPrompt?: string;
    isChatFailed: boolean;
};

export type ChatAreaProps = ChatAreaOwnProps;

const alreadyInitializedChatPrompts = new Set<string>();

const ChatArea: React.FC<ChatAreaProps> = ({
    medicalProfileId,
    isLoadingChat,
    chat,
    isWaitingOnChatMessage,
    isCreatingChat,
    onCreateChat,
    onCreateChatMessage,
    isDeletingChat,
    onDeleteChat,
    chats,
    selectedChatId,
    onSelectChat,
    defaultChatPrompt,
    isChatFailed,
}) => {
    const isMobile = useIsMobileLayout();
    const hasReachedChatLimit = useHasReachedChatMessageLimit(medicalProfileId);
    const [initialChatText, setInitialChatText] = React.useState<string>('');
    const record = useRecordEvent();

    useEffect(() => {
        if (!defaultChatPrompt) return;
        const defaultChatPromptKey = `${medicalProfileId}-${defaultChatPrompt}`;
        if (alreadyInitializedChatPrompts.has(defaultChatPromptKey)) return;

        alreadyInitializedChatPrompts.add(defaultChatPromptKey);
        setInitialChatText(defaultChatPrompt);
        onCreateChat({
            initialPrompt: defaultChatPrompt,
        });
        record('sendChatMessage');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultChatPrompt, medicalProfileId]);

    const centerComponent = (component: React.ReactNode) => (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>{component}</View>
    );

    const renderChatAreaContent = () => {
        if (chat) {
            return <ConversationList key={chat.chatId} chat={chat} isWaitingOnChatMessage={isWaitingOnChatMessage} />;
        } else if ((isCreatingChat || isLoadingChat) && initialChatText) {
            return <InitialMessageSentPlaceholderConversationList initialMessage={initialChatText} />;
        } else if (isCreatingChat || isLoadingChat) {
            return centerComponent(<Loader />);
        } else {
            return centerComponent(
                <EmptyChatState
                    onSuggestionPress={(suggestion) => {
                        setInitialChatText(suggestion);
                        onCreateChat({
                            initialPrompt: suggestion,
                        });
                    }}
                />,
            );
        }
    };

    const renderInButtonContainer = (component: React.ReactNode, width?: number) => (
        <View style={{ width: width || 100 }}>{component}</View>
    );

    return (
        <>
            <Stack
                vertical
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <View style={{ width: '100%', flex: 1 }}>{renderChatAreaContent()}</View>
                <Stack
                    horizontal
                    fullWidth
                    justify="flex-end"
                    style={{
                        paddingLeft: getSpacing(10),
                        paddingRight: getSpacing(10),
                    }}
                >
                    <MedicalProfileUsageIndicator medicalProfileId={medicalProfileId} metricType="chat-message" />
                </Stack>
                <ChatMessageInput
                    style={{ paddingHorizontal: getSpacing(10), marginTop: getSpacing(4) }}
                    isSendingMessage={isCreatingChat || isWaitingOnChatMessage}
                    onSend={(message) => {
                        if (chat) {
                            onCreateChatMessage(message);
                        } else {
                            setInitialChatText(message);
                            onCreateChat({
                                initialPrompt: message,
                            });
                        }
                        record('sendChatMessage');
                    }}
                    isDisabled={isDeletingChat || hasReachedChatLimit || isChatFailed}
                    testID="chat-message-input"
                />
                <PRScrollView style={{ width: '100%', flexGrow: 0 }} horizontal>
                    <Stack
                        horizontal
                        fullWidth
                        style={{ marginTop: getSpacing(2) }}
                        space={4}
                        justify="flex-start"
                        padding={2}
                    >
                        {isMobile ? (
                            <>
                                {renderInButtonContainer(
                                    <Button
                                        variant="ghost"
                                        leftIcon="Plus"
                                        onPress={() => onSelectChat(null)}
                                        title={intl('addChat')}
                                        testID="add-chat-button"
                                        isDisabled={hasReachedChatLimit || !selectedChatId}
                                        size="sm"
                                    />,
                                )}
                                {renderInButtonContainer(
                                    <MobileChatListButton
                                        chats={chats}
                                        onSelectChat={onSelectChat}
                                        selectedChatId={selectedChatId}
                                        isDisabled={isDeletingChat}
                                    />,
                                )}
                            </>
                        ) : null}
                        {!!chat
                            ? renderInButtonContainer(
                                  <DeleteChatButton
                                      onDelete={onDeleteChat}
                                      isDeleting={isDeletingChat}
                                      isDisabled={false}
                                  />,
                              )
                            : null}
                        {renderInButtonContainer(<InformationNotification />, isMobile ? 160 : 260)}
                    </Stack>
                </PRScrollView>
            </Stack>
        </>
    );
};

export default React.memo(ChatArea);
